import React from 'react';

const CompetitorComparison = ({ variant = 'full' }) => {
  const comparisonData = [
    {
      feature: 'Design Fidelity',
      competitor: '"Close enough" design. Fonts and layouts often shift or look "webby." Web-canvas rendering creates approximations, not professional output.',
      personalia: 'Pixel-perfect. If it looks right in InDesign, it looks right in the output.',
      icon: (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      feature: 'Workflow',
      competitor: 'Double work. Designers create in Adobe, then manually "re-build" everything in a clunky web editor with limited capabilities.',
      personalia: 'Native sync. Designers stay in InDesign. Simply tag existing work and upload — no rebuilding required.',
      icon: (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      )
    },
    {
      feature: 'Typography',
      competitor: 'Basic text boxes. No support for GREP styles, nested styles, optical kerning, or complex ligatures.',
      personalia: 'Professional grade. Full support for advanced typographic feature Adobe offers.',
      icon: (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
        </svg>
      )
    },
    {
      feature: 'Print-Ready Output',
      competitor: 'RGB only. No bleed/slug support, no CMYK, no spot colours. Not suitable for professional printing.',
      personalia: 'Press-ready. Full support for CMYK, spot colours, bleeds, and professional printing requirements.',
      icon: (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
        </svg>
      )
    }
  ];

  const painPoints = [
    {
      audience: 'Marketing Teams',
      problem: 'Need more than just "text on an image"? Complex brand guidelines — kerning, specific font weights, text-wrap around objects — that web-canvas tools simply cannot replicate.',
      solution: 'Personalia renders your InDesign templates with full fidelity, not a web browser screenshot. Your brand guidelines stay intact.',
      cta: 'See the difference'
    },
    {
      audience: 'High-Value Proposals',
      problem: 'Your £500k+ proposals look like £15 templates because you\'re restricted to "block-based" web editors that can\'t match your brand\'s prestige.',
      solution: 'Design proposals in InDesign with full creative control, then automate personalisation without sacrificing quality.',
      cta: 'Elevate your proposals'
    },
    {
      audience: 'Developers Fighting HTML-to-PDF',
      problem: 'Tired of fighting CSS to make documents look good across page breaks? Puppeteer and wkhtmltopdf create endless headaches.',
      solution: 'Let designers handle design in InDesign. You just make API calls and receive production-ready, professionally composed documents.',
      cta: 'Escape PDF hell'
    }
  ];

  if (variant === 'compact') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {painPoints.map((point, index) => (
          <div key={index} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
            <div className="text-sm font-semibold text-primary mb-2">{point.audience}</div>
            <p className="text-gray-600 text-sm mb-4">{point.problem}</p>
            <p className="text-gray-800 text-sm font-medium">{point.solution}</p>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-12">
      {/* Comparison Table */}
      <div className="overflow-hidden rounded-xl border border-gray-200">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 w-1/4">Feature</th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-500 w-[37.5%]">
                <span className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Typical Automation Tools
                </span>
              </th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-primary w-[37.5%]">
                <span className="flex items-center gap-2">
                  <svg className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Personalia
                </span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {comparisonData.map((row, index) => (
              <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="text-primary">{row.icon}</div>
                    <span className="font-medium text-gray-900">{row.feature}</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">{row.competitor}</td>
                <td className="px-6 py-4 text-sm text-gray-700 font-medium">{row.personalia}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Key Differentiator Callout */}
      <div className="bg-primary rounded-xl p-8 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="text-2xl font-bold mb-4">The Fundamental Difference</h3>
          <p className="text-lg opacity-90 mb-6">
            Web-based tools use a browser to "take a screenshot" of your design. 
            Personalia renders your InDesign templates server-side with <span className="font-semibold">full fidelity</span>. 
            One is a picture; the other is a professional asset.
          </p>
          <div className="inline-flex items-center gap-2 bg-white bg-opacity-20 rounded-full px-4 py-2 text-sm">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Trusted by enterprise teams worldwide
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompetitorComparison;
